import { useMemo } from 'react';

import { useAppStore, type IAppStore } from '@/store';
import useLibrarySectionsQuery from '@/hooks/queries/useLibrarySectionsQuery';
import useNewPresetsQuery from '@/hooks/queries/useNewPresetsQuery';
import { NEW_TAB_SLUG } from '@/store/homeTabs/types';

import type { LibraryItem } from '@/types';

const selector = (store: IAppStore) => ({
  activeTabSlug: store.activeTabSlug,
  entrySlug: store.presetFeedEntrySlug,
});

const usePresetFeedData = (): {
  items: LibraryItem[];
  initialIndex: number;
  isLoading: boolean;
} => {
  const { activeTabSlug, entrySlug } = useAppStore(selector);
  const { data: sections, isPending } = useLibrarySectionsQuery();

  // Cards in the "New" tab live in a different cache than the editorial rows,
  // and `activeTabSlug` is the only clue which one the feed was opened from.
  const isNewTab = activeTabSlug === NEW_TAB_SLUG;
  const { data: newSection, isPending: isNewPending } =
    useNewPresetsQuery(isNewTab);

  return useMemo(() => {
    // Three tiers, each a fallback for the one above:
    //
    //  1. `activeTabSlug` names a section AND that section contains the entry
    //     slug — the normal path, every home-grid tap and deeplink
    //     (`preset.category` is the section slug, set by
    //     `buildPresetFromLibraryItem`). The membership check is what keeps a
    //     named-but-wrong category from winning: a deeplink can pair a real
    //     preset with a real section that doesn't hold it
    //     (`presetId=back-to-school-outfits&genaicategory=ai-social-virals` —
    //     that preset lives in `back-to-school`), and `tryOpenPresetFeedFromDeeplink`
    //     stores the URL's category unvalidated. Matching on the name alone put
    //     the feed in the named section, `findIndex` missed, and it silently
    //     opened that section's FIRST slide instead of the requested preset.
    //     When there is no entry slug there is nothing to be consistent with, so
    //     the named section is taken as-is.
    //  2. The named section is absent or doesn't hold the entry slug. Some
    //     callers store an editorial tag instead of a section slug — the
    //     trending payload's `category` is one (`fashion-try-ons`,
    //     `product-marketing`), and search passes `genaicategory`. The entry slug
    //     names exactly one preset, so it finds the right section without
    //     trusting the tag at all. Without this the lookup missed, the feed came
    //     up empty, and `PresetFeedScreen` bounced straight back to the library —
    //     a tap that visibly did nothing.
    //  3. Neither resolves. Prefer the named section if it exists at all (a real
    //     category whose preset is only reachable via the detail fetch), else the
    //     first section rather than an empty feed. The feed then opens at index 0,
    //     and `loadActivePreset` swaps `selectedPreset` to that slide — the tapped
    //     preset is lost, so this really is a last resort, not a second choice.
    //
    //     Gated on `activeTabSlug` being set: a `Screen.Create` with none was
    //     never a request to page through a section, and substituting the first
    //     one would hijack it — a preset opened with no category (the
    //     `preset.category &&` guards in `presetHandler` / `usePresetFromRoute`)
    //     would get replaced by whatever sits at index 0. Empty, and the bounce
    //     back to the library, stays the correct answer there.
    const namedSection = sections?.find(
      (candidate) => candidate.slug === activeTabSlug
    );
    const holdsEntry = (candidate: { items: LibraryItem[] }) =>
      !!entrySlug && candidate.items.some((item) => item.slug === entrySlug);

    const section =
      (namedSection && (!entrySlug || holdsEntry(namedSection))
        ? namedSection
        : undefined) ??
      (entrySlug ? sections?.find(holdsEntry) : undefined) ??
      namedSection ??
      (activeTabSlug ? sections?.[0] : undefined);

    const items = isNewTab ? newSection?.items ?? [] : section?.items ?? [];
    const foundIndex = items.findIndex((item) => item.slug === entrySlug);

    return {
      items,
      initialIndex: foundIndex >= 0 ? foundIndex : 0,
      isLoading:
        Boolean(activeTabSlug) && (isNewTab ? isNewPending : isPending),
    };
  }, [
    sections,
    newSection,
    isNewTab,
    entrySlug,
    activeTabSlug,
    isPending,
    isNewPending,
  ]);
};

export default usePresetFeedData;
